import { useState, useEffect, useCallback } from 'react';
import { storage, db } from '../../../../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
	collection,
	addDoc,
	query,
	where,
	getDocs,
	Timestamp,
} from 'firebase/firestore';

/**
 * Student materials for the drawer / rail — the Firebase half of the old
 * StudentMaterials.js component, extracted as a logic MOVE (design handoff Ph 5).
 * Presentation is rebuilt to the drawer's file-row grammar; full management
 * (rename, retype, delete) stays on the student profile, one tap away.
 *
 * Upload here is intentionally one-tap: a file picker, the title inferred from
 * the filename and the type from the mime, no modal form. Same Storage path and
 * `studentMaterials` doc shape the profile uploader writes, so the two surfaces
 * read each other's files.
 */

/** Stored `type` enum (matches the profile uploader's options). */
const inferType = (file) => {
	const t = file.type || '';
	if (t.startsWith('audio/')) return 'audio-track';
	if (t.startsWith('video/')) return 'video';
	if (t === 'application/pdf') return 'document';
	return 'other';
};

const useStudentMaterials = (studentId, studentName) => {
	const [materials, setMaterials] = useState([]);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState('');

	const reload = useCallback(async () => {
		if (!studentId) return;
		setLoading(true);
		setError('');
		try {
			// Order client-side by upload time so a missing index can't blank the
			// list; the query itself is a simple studentId equality.
			const snap = await getDocs(
				query(collection(db, 'studentMaterials'), where('studentId', '==', studentId))
			);
			const rows = snap.docs
				.map((d) => ({ id: d.id, ...d.data() }))
				.sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0));
			setMaterials(rows);
		} catch (err) {
			console.error('Error loading student materials:', err);
			setError('Could not load materials.');
		} finally {
			setLoading(false);
		}
	}, [studentId]);

	useEffect(() => {
		if (studentId) reload();
		else setMaterials([]);
	}, [studentId, reload]);

	const upload = useCallback(
		(file) => {
			if (!file || !studentId) return;
			setUploading(true);
			setProgress(0);
			setError('');
			const type = inferType(file);
			const storageRef = ref(storage, `students/${studentId}/${type}/${Date.now()}_${file.name}`);
			const task = uploadBytesResumable(storageRef, file);
			task.on(
				'state_changed',
				(snap) => setProgress((snap.bytesTransferred / snap.totalBytes) * 100),
				(err) => {
					console.error('Material upload error:', err);
					setError('Upload failed. Try again.');
					setUploading(false);
				},
				async () => {
					try {
						const downloadURL = await getDownloadURL(task.snapshot.ref);
						await addDoc(collection(db, 'studentMaterials'), {
							studentId,
							studentName: studentName || 'Unknown Student',
							title: file.name.replace(/\.[^/.]+$/, ''),
							type,
							description: '',
							fileName: file.name,
							fileSize: file.size,
							fileType: file.type,
							storagePath: storageRef.fullPath,
							downloadURL,
							uploadedAt: Timestamp.now(),
						});
						await reload();
					} catch (err) {
						console.error('Error saving material metadata:', err);
						setError('Upload saved to storage but not recorded. Refresh to check.');
					} finally {
						setUploading(false);
						setProgress(0);
					}
				}
			);
		},
		[studentId, studentName, reload]
	);

	return { materials, loading, uploading, progress, error, upload, reload };
};

export default useStudentMaterials;
